/**
 * The active piece: collision, movement, rotation, hold and the ghost.
 *
 * Nothing here reads the clock or scores anything; locking lives in `lock.ts`.
 * Keeping this module free of scoring is what lets `garbage.ts` move a piece
 * without importing half the engine back.
 */
import { isBlocked } from './board.js';
import { drawNext } from './bag.js';
import { kicksFor } from './kicks.js';
import { pieceCells, pieceCellsAt, rotateState } from './pieces.js';
import type {
  ActivePiece,
  GameState,
  PieceKind,
  RotationDirection,
  RotationState,
  Vec2,
} from './types.js';

/** The piece in a slot, or `null` when the slot is empty. */
export function activePiece(state: GameState, slot = 0): ActivePiece | null {
  return state.pieces[slot] ?? null;
}

/**
 * Whether a piece of this shape at this position overlaps the *board*.
 *
 * Walls, floor, ceiling and stack. This is the whole of collision for a
 * one-slot well, and it is what a block-out is still measured against even in
 * a shared one — see `spawnPiece`.
 */
export function collidesBoard(
  state: GameState,
  kind: PieceKind,
  rotation: RotationState,
  x: number,
  y: number,
): boolean {
  for (const [cx, cy] of pieceCells(kind, rotation)) {
    if (isBlocked(state.board, x + cx, y + cy)) return true;
  }
  return false;
}

/**
 * Whether a piece of this shape at this position overlaps another slot's
 * active piece.
 *
 * Cell against cell, sixteen comparisons per other piece. A set would be
 * tidier and slower: this runs inside `dropDistance`'s loop, which the
 * determinism suite runs millions of times.
 */
function collidesPieces(
  state: GameState,
  slot: number,
  kind: PieceKind,
  rotation: RotationState,
  x: number,
  y: number,
): boolean {
  if (state.pieces.length < 2) return false;
  const cells = pieceCells(kind, rotation);
  for (const [index, other] of state.pieces.entries()) {
    if (index === slot || other === null) continue;
    const otherCells = pieceCells(other.kind, other.rotation);
    for (const [cx, cy] of cells) {
      const ax = x + cx;
      const ay = y + cy;
      for (const [ox, oy] of otherCells) {
        if (ax === other.x + ox && ay === other.y + oy) return true;
      }
    }
  }
  return false;
}

/**
 * Whether a piece of this shape at this position overlaps anything.
 *
 * The board always, and — when `slot` is given — every *other* slot's active
 * piece. `docs/prompts/06-coop.md`: "Piece-versus-piece collision belongs in
 * the engine, not the server."
 *
 * Omitting `slot` asks the board-only question, which is what phase 01's
 * signature meant and what a caller outside a co-op well wants. Passing it in
 * a one-slot game is the same question, because there is no other piece to
 * find: that is why nothing about single-piece play — or its hash — moved when
 * this argument appeared. See `docs/decisions/0029`.
 */
export function collides(
  state: GameState,
  kind: PieceKind,
  rotation: RotationState,
  x: number,
  y: number,
  slot?: number,
): boolean {
  if (collidesBoard(state, kind, rotation, x, y)) return true;
  if (slot === undefined) return false;
  return collidesPieces(state, slot, kind, rotation, x, y);
}

/**
 * Column of a spawning piece's bounding box.
 *
 * One slot centres the well, exactly as it always has. More than one divides
 * the well into equal regions and centres each slot's four-wide box in its
 * own — which is what makes `shared`'s two pieces spawn clear of each other in
 * a ten-wide well (columns 0..3 and 5..8) and what puts `wide`'s two players
 * one per half of a sixteen-wide one (2..5 and 10..13).
 */
export function spawnColumn(state: GameState, slot = 0): number {
  const slots = state.pieces.length;
  const width = state.config.width;
  if (slots <= 1) return Math.floor(width / 2) - 2;
  const centre = Math.floor((width * (2 * slot + 1)) / (2 * slots));
  const highest = Math.max(0, width - 4);
  return Math.min(highest, Math.max(0, centre - 2));
}

/**
 * Row of a spawning piece's bounding box.
 *
 * One row up from the visible field, which puts the top row of a J, L, O, S, T
 * or Z in the hidden buffer and its bottom row on visible row 0 — exactly where
 * the reference prototype spawns them.
 */
export function spawnRow(state: GameState): number {
  return state.config.hiddenRows - 1;
}

/**
 * Where a spawning piece actually goes, once the partner is taken into account.
 *
 * The rule, in order, and the order is the point:
 *
 * 1. The slot's own spawn column, if nothing is there. Always the answer in a
 *    one-slot well.
 * 2. If the *board* blocks that column, stay there. That is a block-out and it
 *    must stay one — searching sideways for a gap would turn a lost game into a
 *    saved one and would change how every solo mode ends.
 * 3. Otherwise the partner's piece is what is in the way, and that is not the
 *    player's doing: step sideways — one right, one left, two right, two
 *    left — and take the first column free of both. A top-out because somebody
 *    else parked over your spawn is a punishment for the wrong person.
 * 4. Nowhere free at all: back to the spawn column and top out.
 */
function spawnColumnFor(
  state: GameState,
  slot: number,
  kind: PieceKind,
  rotation: RotationState,
  y: number,
): number {
  const base = spawnColumn(state, slot);
  if (!collides(state, kind, rotation, base, y, slot)) return base;
  if (collidesBoard(state, kind, rotation, base, y)) return base;

  for (let offset = 1; offset <= state.config.width; offset++) {
    for (const dx of [offset, -offset]) {
      const x = base + dx;
      if (!collides(state, kind, rotation, x, y, slot)) return x;
    }
  }
  return base;
}

/**
 * Put a piece in a slot, drawing from the queue when `kind` is null.
 *
 * A spawn that already collides is a block-out: the state is marked topped out
 * and the piece is left where it is, so a renderer can still show what killed
 * the player.
 */
export function spawnPiece(state: GameState, slot: number, kind: PieceKind | null): void {
  const letter = kind ?? drawNext(state);
  const y = spawnRow(state);
  const piece: ActivePiece = {
    kind: letter,
    rotation: 0,
    x: spawnColumnFor(state, slot, letter, 0, y),
    y,
    lockTicks: 0,
    lockResets: 0,
    gravityUnits: 0,
    lastActionWasRotation: false,
  };
  state.pieces[slot] = piece;
  state.holdAvailable[slot] = true;
  if (collides(state, piece.kind, piece.rotation, piece.x, piece.y, slot)) {
    state.topOut = true;
  }
}

/**
 * Translate the piece. Returns false when the target overlaps something.
 *
 * A horizontal move resets the lock delay, up to `maxLockResets` times per
 * piece. A vertical move does not: a piece that moved down is either no longer
 * resting — in which case the lock timer is cleared by the grounded check — or
 * it just landed, and its timer was already zero.
 *
 * Any displacement clears `lastActionWasRotation`, which is what the T-spin
 * rule reads. A *zero* displacement leaves it alone; that matters, because a
 * hard drop of distance zero is how almost every T-spin is finished.
 */
export function tryMove(state: GameState, slot: number, dx: number, dy: number): boolean {
  const piece = activePiece(state, slot);
  if (piece === null) return false;
  if (collides(state, piece.kind, piece.rotation, piece.x + dx, piece.y + dy, slot)) return false;

  piece.x += dx;
  piece.y += dy;
  if (dx !== 0 || dy !== 0) piece.lastActionWasRotation = false;
  if (dx !== 0 && piece.lockResets < state.config.maxLockResets) {
    piece.lockTicks = 0;
    piece.lockResets += 1;
  }
  return true;
}

/**
 * Rotate the piece, trying each SRS kick offset in order.
 *
 * O is refused outright, as in the reference prototype: it has no meaningful
 * rotation, so turning it must not reset the lock delay either.
 */
export function tryRotate(state: GameState, slot: number, direction: RotationDirection): boolean {
  const piece = activePiece(state, slot);
  if (piece === null || piece.kind === 'O') return false;

  const from = piece.rotation;
  const to = rotateState(from, direction);
  for (const [dx, dy] of kicksFor(piece.kind, from, to)) {
    const x = piece.x + dx;
    const y = piece.y + dy;
    if (collides(state, piece.kind, to, x, y, slot)) continue;
    piece.x = x;
    piece.y = y;
    piece.rotation = to;
    piece.lastActionWasRotation = true;
    if (piece.lockResets < state.config.maxLockResets) {
      piece.lockTicks = 0;
      piece.lockResets += 1;
    }
    return true;
  }
  return false;
}

/** How many rows the piece can still fall. Zero when it is resting. */
export function dropDistance(state: GameState, slot = 0): number {
  const piece = activePiece(state, slot);
  if (piece === null) return 0;
  let distance = 0;
  while (!collides(state, piece.kind, piece.rotation, piece.x, piece.y + distance + 1, slot)) {
    distance += 1;
  }
  return distance;
}

/** True when the piece is resting on the stack, the floor, or the partner's piece. */
export function isGrounded(state: GameState, slot: number): boolean {
  const piece = activePiece(state, slot);
  if (piece === null) return false;
  return collides(state, piece.kind, piece.rotation, piece.x, piece.y + 1, slot);
}

/**
 * Row the piece's bounding box would occupy if dropped now.
 *
 * This is the ghost piece, for the renderer. Pure: it reads state and returns a
 * number. `null` means the slot has no piece.
 */
export function ghostLandingRow(state: GameState, slot = 0): number | null {
  const piece = activePiece(state, slot);
  if (piece === null) return null;
  return piece.y + dropDistance(state, slot);
}

/** Board cells the ghost piece occupies, for a renderer that wants them directly. */
export function ghostCells(state: GameState, slot = 0): readonly Vec2[] {
  const piece = activePiece(state, slot);
  if (piece === null) return [];
  return pieceCellsAt(piece.kind, piece.rotation, piece.x, piece.y + dropDistance(state, slot));
}

/** Board cells the active piece occupies right now. */
export function activePieceCells(state: GameState, slot = 0): readonly Vec2[] {
  const piece = activePiece(state, slot);
  if (piece === null) return [];
  return pieceCellsAt(piece.kind, piece.rotation, piece.x, piece.y);
}

/**
 * Swap the active piece with the held one. Once per piece.
 *
 * The incoming piece spawns fresh: spawn position, spawn rotation, lock delay
 * and resets all cleared.
 */
export function holdPiece(state: GameState, slot = 0): boolean {
  if (state.holdAvailable[slot] !== true) return false;
  const piece = activePiece(state, slot);
  if (piece === null) return false;

  const stashed = state.holds[slot] ?? null;
  state.holds[slot] = piece.kind;
  spawnPiece(state, slot, stashed);
  state.holdAvailable[slot] = false;
  return true;
}
